import {
  LitElement, html, nothing,
  type TemplateResult, type CSSResultGroup, type PropertyValues,
} from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { emit } from '../utils/dom.utils.js';
import styles from './styles.js';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GalleriaResponsiveOption {
  /** Max viewport width (px) this breakpoint applies up to. */
  breakpoint: string;
  numVisible: number;
}

// ── Internal constants ────────────────────────────────────────────────────────

const THUMB_W   = 80;
const THUMB_H   = 60;
const THUMB_GAP = 8;

/**
 * uwc-galleria
 *
 * Feature-rich image gallery component with thumbnails, dot indicators,
 * caption overlay, fullscreen mode, autoplay, swipe navigation, and
 * custom item / thumbnail / caption render templates.
 * Production-grade image gallery component.
 *
 * @attr {unknown[]}    items                          - Data array. Set via JS property.
 * @attr {number}       active-index                   - Currently active item index. Default: 0.
 * @attr {number}       num-visible                    - Number of thumbnails visible at once. Default: 4.
 * @attr {boolean}      circular                       - Wrap-around navigation.
 * @attr {boolean}      auto-play                      - Enable autoplay slideshow.
 * @attr {number}       auto-play-interval             - Autoplay interval in ms. Default: 4000.
 * @attr {boolean}      show-thumbnails                - Show thumbnail strip.
 * @attr {boolean}      show-item-navigators           - Show prev/next nav buttons always.
 * @attr {boolean}      show-item-navigators-on-hover  - Show prev/next nav buttons only on hover.
 * @attr {boolean}      show-indicators                - Show dot page indicators.
 * @attr {boolean}      show-indicators-on-item        - Overlay indicators on the item instead of below.
 * @attr {boolean}      show-caption                   - Show caption overlay on the item.
 * @attr {string}       thumbnails-position            - bottom | top | left | right. Default: bottom.
 * @attr {string}       indicators-position            - bottom | top. Default: bottom.
 * @attr {boolean}      fullscreen-enabled             - Show a fullscreen toggle button.
 * @attr {boolean}      change-on-indicator-hover      - Change active item when hovering an indicator dot.
 *
 * @prop {(item, index) => TemplateResult}  itemTemplate      - Custom item render function.
 * @prop {(item, index) => TemplateResult}  thumbnailTemplate - Custom thumbnail render function.
 * @prop {(item, index) => TemplateResult}  captionTemplate   - Custom caption render function.
 * @prop {GalleriaResponsiveOption[]}       responsiveOptions - Responsive breakpoint overrides for numVisible.
 *
 * @slot header  - Content above the gallery.
 * @slot footer  - Content below the gallery.
 *
 * @fires uwc-change           - Active index changed. detail: { index: number }
 * @fires uwc-fullscreen-open  - Fullscreen dialog opened.
 * @fires uwc-fullscreen-close - Fullscreen dialog closed.
 *
 * @cssprop --uwc-gal-item-min-height  - Minimum height of the item viewport. Default: 300px.
 * @cssprop --uwc-gal-duration         - Fade animation duration. Default: 300ms.
 * @cssprop --uwc-gal-thumb-bg         - Background of the thumbnail strip. Default: #111.
 * @cssprop --uwc-gal-thumb-w          - Thumbnail width. Default: 80px.
 * @cssprop --uwc-gal-thumb-h          - Thumbnail height. Default: 60px.
 * @cssprop --uwc-gal-thumb-active     - Active thumbnail border colour. Default: #fff.
 */
export class UwcGalleria extends LitElement {

  static override styles: CSSResultGroup = styles;

  // ── Public properties ───────────────────────────────────────────────────────

  @property({ type: Array })
  items: unknown[] = [];

  @property({ type: Number, attribute: 'active-index' })
  activeIndex = 0;

  @property({ type: Number, attribute: 'num-visible' })
  numVisible = 4;

  @property({ type: Boolean, reflect: true })
  circular = false;

  @property({ type: Boolean, attribute: 'auto-play' })
  autoPlay = false;

  @property({ type: Number, attribute: 'auto-play-interval' })
  autoPlayInterval = 4000;

  @property({ type: Boolean, attribute: 'show-thumbnails' })
  showThumbnails = false;

  @property({ type: Boolean, attribute: 'show-item-navigators' })
  showItemNavigators = false;

  @property({ type: Boolean, attribute: 'show-item-navigators-on-hover' })
  showItemNavigatorsOnHover = false;

  @property({ type: Boolean, attribute: 'show-indicators' })
  showIndicators = false;

  @property({ type: Boolean, attribute: 'show-indicators-on-item' })
  showIndicatorsOnItem = false;

  @property({ type: Boolean, attribute: 'show-caption' })
  showCaption = false;

  @property({ attribute: 'thumbnails-position' })
  thumbnailsPosition: 'bottom' | 'top' | 'left' | 'right' = 'bottom';

  @property({ attribute: 'indicators-position' })
  indicatorsPosition: 'bottom' | 'top' = 'bottom';

  @property({ type: Boolean, attribute: 'fullscreen-enabled' })
  fullscreenEnabled = false;

  @property({ type: Boolean, attribute: 'change-on-indicator-hover' })
  changeOnIndicatorHover = false;

  /** Custom item render function. `(item: unknown, index: number) => TemplateResult` */
  itemTemplate?: (item: unknown, index: number) => TemplateResult;

  /** Custom thumbnail render function. `(item: unknown, index: number) => TemplateResult` */
  thumbnailTemplate?: (item: unknown, index: number) => TemplateResult;

  /** Custom caption render function. `(item: unknown, index: number) => TemplateResult` */
  captionTemplate?: (item: unknown, index: number) => TemplateResult;

  /** Responsive breakpoint overrides. */
  responsiveOptions: GalleriaResponsiveOption[] = [];

  // ── Internal state ──────────────────────────────────────────────────────────

  @state() private _activeIndex = 0;
  @state() private _isFullscreen = false;
  @state() private _isAnimating = false;
  @state() private _liveLabel = '';
  @state() private _effectiveNumVisible = 4;
  @state() private _thumbAtStart = true;
  @state() private _thumbAtEnd = false;

  @query('dialog')
  private _dialog?: HTMLDialogElement;

  private _autoplayTimer?: ReturnType<typeof setInterval>;
  private _resizeObserver?: ResizeObserver;
  private _pointerStartX = 0;
  private _pointerStartY = 0;
  private _pointerDragging = false;
  private _isSwiping = false;

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  override connectedCallback() {
    super.connectedCallback();
    this._activeIndex = this.activeIndex;
    this._applyResponsive();
    this._resizeObserver = new ResizeObserver(() => this._applyResponsive());
    this._resizeObserver.observe(this);
    if (this.autoPlay) this._startAutoplay();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._resizeObserver?.disconnect();
    this._stopAutoplay();
  }

  override firstUpdated() {
    this._updateThumbScrollState();
  }

  override updated(changed: PropertyValues) {
    if (changed.has('activeIndex')) {
      this._setActiveIndex(this.activeIndex, false);
    }
    if (changed.has('autoPlay') || changed.has('autoPlayInterval')) {
      this._stopAutoplay();
      if (this.autoPlay) this._startAutoplay();
    }
    if (changed.has('items') || changed.has('numVisible')) {
      this._applyResponsive();
    }
    if (changed.has('_activeIndex')) {
      this._scrollActiveThumbIntoView();
    }
  }

  // ── Responsive ──────────────────────────────────────────────────────────────

  private _applyResponsive() {
    const w = this.offsetWidth;
    let nv = this.numVisible;

    if (this.responsiveOptions.length) {
      const sorted = [...this.responsiveOptions].sort(
        (a, b) => parseInt(b.breakpoint) - parseInt(a.breakpoint),
      );
      for (const opt of sorted) {
        if (w <= parseInt(opt.breakpoint)) {
          nv = opt.numVisible;
        }
      }
    }

    this._effectiveNumVisible = Math.max(1, Math.min(nv, this.items.length || 1));
    this._updateThumbScrollState();
  }

  // ── Navigation ──────────────────────────────────────────────────────────────

  private _setActiveIndex(index: number, animate = true) {
    const len = this.items.length;
    if (len === 0) return;

    let next: number;
    if (this.circular) {
      next = ((index % len) + len) % len;
    } else {
      next = Math.max(0, Math.min(index, len - 1));
    }

    if (next === this._activeIndex) return;

    if (animate) {
      this._isAnimating = true;
      setTimeout(() => { this._isAnimating = false; }, 400);
    }

    this._activeIndex = next;
    this._liveLabel = `Image ${next + 1} of ${len}`;
    emit(this, 'uwc-change', { index: next });
  }

  /** Navigate to the previous item. */
  prev() { this._setActiveIndex(this._activeIndex - 1); }

  /** Navigate to the next item. */
  next() { this._setActiveIndex(this._activeIndex + 1); }

  /** Navigate to a specific item index (0-based). */
  goTo(index: number) { this._setActiveIndex(index); }

  get isPrevDisabled(): boolean {
    return !this.circular && this._activeIndex === 0;
  }

  get isNextDisabled(): boolean {
    return !this.circular && this._activeIndex >= this.items.length - 1;
  }

  // ── Autoplay ─────────────────────────────────────────────────────────────────

  private _startAutoplay() {
    if (!this.autoPlay || this.autoPlayInterval <= 0) return;
    this._autoplayTimer = setInterval(() => {
      const len = this.items.length;
      if (len === 0) return;
      const next = this.circular
        ? (this._activeIndex + 1) % len
        : this._activeIndex < len - 1 ? this._activeIndex + 1 : 0;
      this._setActiveIndex(next);
    }, this.autoPlayInterval);
  }

  private _stopAutoplay() {
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = undefined;
    }
  }

  // ── Thumbnail scroll ─────────────────────────────────────────────────────────

  private _scrollActiveThumbIntoView() {
    requestAnimationFrame(() => {
      this.renderRoot
        .querySelector<HTMLElement>('.uwc-gal__thumb-item.is-active')
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }

  private _scrollThumbs(dir: 'prev' | 'next') {
    const viewport = this.renderRoot.querySelector<HTMLElement>('.uwc-gal__thumb-viewport');
    if (!viewport) return;
    const isVert = this.thumbnailsPosition === 'left' || this.thumbnailsPosition === 'right';
    const step = this._effectiveNumVisible * ((isVert ? THUMB_H : THUMB_W) + THUMB_GAP);
    if (isVert) {
      viewport.scrollBy({ top: dir === 'next' ? step : -step, behavior: 'smooth' });
    } else {
      viewport.scrollBy({ left: dir === 'next' ? step : -step, behavior: 'smooth' });
    }
  }

  private _updateThumbScrollState() {
    const viewport = this.renderRoot.querySelector<HTMLElement>('.uwc-gal__thumb-viewport');
    if (!viewport) return;
    const isVert = this.thumbnailsPosition === 'left' || this.thumbnailsPosition === 'right';
    const scrollPos  = isVert ? viewport.scrollTop  : viewport.scrollLeft;
    const scrollSize = isVert ? viewport.scrollHeight : viewport.scrollWidth;
    const clientSize = isVert ? viewport.clientHeight : viewport.clientWidth;

    this._thumbAtStart = scrollPos <= 1;
    this._thumbAtEnd   = scrollPos + clientSize >= scrollSize - 1;
  }

  private _onThumbScroll() {
    this._updateThumbScrollState();
  }

  // ── Event handlers ───────────────────────────────────────────────────────────

  private _onKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        this.prev();
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        this.next();
        break;
      case 'Home':
        e.preventDefault();
        this._setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        this._setActiveIndex(this.items.length - 1);
        break;
      case 'Escape':
        if (this._isFullscreen) this._closeFullscreen();
        break;
    }
  }

  private _onPointerDown(e: PointerEvent) {
    if (e.button !== 0) return;
    this._pointerStartX = e.clientX;
    this._pointerStartY = e.clientY;
    this._pointerDragging = true;
    this._isSwiping = false;
    // Don't call setPointerCapture here — doing so on every pointerdown
    // would intercept pointer events for buttons/indicators inside the
    // wrapper, preventing their click events from firing. Capture is
    // deferred to _onPointerMove once horizontal drag is confirmed.
    this._stopAutoplay();
  }

  private _onPointerMove(e: PointerEvent) {
    if (!this._pointerDragging || this._isSwiping) return;
    const dx = Math.abs(e.clientX - this._pointerStartX);
    const dy = Math.abs(e.clientY - this._pointerStartY);
    // Only lock in swipe mode when horizontal movement is dominant and
    // has crossed the 8px threshold to distinguish a drag from a tap.
    if (dx > 8 && dx > dy) {
      this._isSwiping = true;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
  }

  private _onPointerUp(e: PointerEvent) {
    if (!this._pointerDragging) return;
    this._pointerDragging = false;
    if (this._isSwiping) {
      this._isSwiping = false;
      const dx = e.clientX - this._pointerStartX;
      const dy = e.clientY - this._pointerStartY;
      const threshold = 50;
      if (Math.abs(dx) >= Math.abs(dy) && Math.abs(dx) >= threshold) {
        dx < 0 ? this.next() : this.prev();
      }
    }
    if (this.autoPlay) this._startAutoplay();
  }

  private _onPointerCancel() {
    this._pointerDragging = false;
    this._isSwiping = false;
    if (this.autoPlay) this._startAutoplay();
  }

  // ── Fullscreen ───────────────────────────────────────────────────────────────

  private async _openFullscreen() {
    this._isFullscreen = true;
    await this.updateComplete;
    this._dialog?.showModal();
    // Focus the close button for keyboard accessibility
    this._dialog?.querySelector<HTMLElement>('.uwc-gal__close-btn uwc-button')?.focus();
    emit(this, 'uwc-fullscreen-open', {});
  }

  private _closeFullscreen() {
    this._dialog?.close();
    this._isFullscreen = false;
    emit(this, 'uwc-fullscreen-close', {});
  }

  // ── Default templates ────────────────────────────────────────────────────────

  private _getItemSrc(item: unknown): string {
    const it = item as Record<string, unknown>;
    return String(it['itemImageSrc'] ?? it['src'] ?? it['image'] ?? it['url'] ?? '');
  }

  private _getItemAlt(item: unknown): string {
    const it = item as Record<string, unknown>;
    return String(it['alt'] ?? it['title'] ?? '');
  }

  private _defaultItemTpl(item: unknown): TemplateResult {
    const src = this._getItemSrc(item);
    const alt = this._getItemAlt(item);
    return html`<img class="uwc-gal__default-img" src=${src} alt=${alt} loading="lazy" decoding="async" draggable="false" />`;
  }

  private _defaultThumbTpl(item: unknown): TemplateResult {
    const it  = item as Record<string, unknown>;
    const src = String(it['thumbnailImageSrc'] ?? it['thumb'] ?? it['thumbnail'] ?? this._getItemSrc(item));
    const alt = this._getItemAlt(item);
    return html`<img class="uwc-gal__default-thumb" src=${src} alt=${alt} loading="lazy" decoding="async" draggable="false" />`;
  }

  private _defaultCaptionTpl(item: unknown): TemplateResult {
    const it    = item as Record<string, unknown>;
    const title = it['title'] ? String(it['title']) : '';
    const desc  = it['description'] ? String(it['description']) : (it['alt'] ? String(it['alt']) : '');
    return html`
      ${title ? html`<p class="uwc-gal__caption-title">${title}</p>` : nothing}
      ${desc  ? html`<p class="uwc-gal__caption-desc">${desc}</p>`   : nothing}
    `;
  }

  // ── Render helpers ───────────────────────────────────────────────────────────

  private _renderNavBtn(dir: 'prev' | 'next'): TemplateResult {
    const isPrev  = dir === 'prev';
    const icon    = isPrev ? 'chevron-left' : 'chevron-right';
    const label   = isPrev ? 'Previous image' : 'Next image';
    const disabled = isPrev ? this.isPrevDisabled : this.isNextDisabled;
    const handler  = isPrev ? () => this.prev() : () => this.next();

    return html`
      <button
        type="button"
        class=${classMap({ 'uwc-gal__nav-btn': true, 'uwc-gal__nav-btn--disabled': disabled })}
        aria-label=${label}
        ?disabled=${disabled}
        @click=${handler}
      >
        <uwc-icon name=${icon} size="1.25em"></uwc-icon>
      </button>
    `;
  }

  private _renderIndicators(inItem = false): TemplateResult {
    const posClass = `uwc-gal__indicators--${this.indicatorsPosition}`;
    const classes = {
      'uwc-gal__indicators': true,
      'uwc-gal__indicators--on-item': inItem,
      'uwc-gal__indicators--top':    inItem && this.indicatorsPosition === 'top',
      'uwc-gal__indicators--bottom': inItem && this.indicatorsPosition === 'bottom',
      [posClass]: !inItem,
    };

    return html`
      <div
        class=${classMap(classes)}
        role="tablist"
        aria-label="Gallery indicators"
      >
        ${repeat(
          this.items,
          (_, i) => i,
          (_, i) => html`
            <button
              type="button"
              class=${classMap({ 'uwc-gal__indicator': true, 'is-active': i === this._activeIndex })}
              role="tab"
              aria-selected=${i === this._activeIndex ? 'true' : 'false'}
              aria-label="Go to image ${i + 1}"
              @click=${() => this._setActiveIndex(i)}
              @mouseenter=${this.changeOnIndicatorHover ? () => this._setActiveIndex(i) : nothing}
            ></button>
          `,
        )}
      </div>
    `;
  }

  private _renderThumbnailStrip(): TemplateResult {
    const isVert = this.thumbnailsPosition === 'left' || this.thumbnailsPosition === 'right';

    return html`
      <div class=${classMap({ 'uwc-gal__thumbnails': true, 'uwc-gal__thumbnails--vert': isVert })}>
        <!-- Scroll prev -->
        <uwc-button
          size="small"
          text
          icon-only
          icon=${isVert ? 'chevron-up' : 'chevron-left'}
          aria-label="Scroll thumbnails backward"
          ?disabled=${this._thumbAtStart}
          @uwc-click=${() => this._scrollThumbs('prev')}
        ></uwc-button>

        <!-- Viewport + track -->
        <div class="uwc-gal__thumb-viewport" @scroll=${this._onThumbScroll}>
          <div class=${classMap({ 'uwc-gal__thumb-track': true, 'uwc-gal__thumb-track--vert': isVert })}>
            ${repeat(
              this.items,
              (_, i) => i,
              (item, i) => html`
                <button
                  type="button"
                  class=${classMap({ 'uwc-gal__thumb-item': true, 'is-active': i === this._activeIndex })}
                  aria-label="View image ${i + 1}"
                  aria-pressed=${i === this._activeIndex ? 'true' : 'false'}
                  @click=${() => this._setActiveIndex(i)}
                >
                  ${this.thumbnailTemplate
                    ? this.thumbnailTemplate(item, i)
                    : this._defaultThumbTpl(item)}
                </button>
              `,
            )}
          </div>
        </div>

        <!-- Scroll next -->
        <uwc-button
          size="small"
          text
          icon-only
          icon=${isVert ? 'chevron-down' : 'chevron-right'}
          aria-label="Scroll thumbnails forward"
          ?disabled=${this._thumbAtEnd}
          @uwc-click=${() => this._scrollThumbs('next')}
        ></uwc-button>
      </div>
    `;
  }

  private _renderItemSection(inFullscreen = false): TemplateResult {
    const showNav   = this.showItemNavigators || this.showItemNavigatorsOnHover;
    const hoverNav  = this.showItemNavigatorsOnHover && !this.showItemNavigators;
    const activeItem = this.items[this._activeIndex];

    // Indicators at top of item section (not on-item)
    const topIndicators = this.showIndicators && !this.showIndicatorsOnItem && this.indicatorsPosition === 'top'
      ? this._renderIndicators(false)
      : nothing;

    // Indicators at bottom of item section (not on-item)
    const bottomIndicators = this.showIndicators && !this.showIndicatorsOnItem && this.indicatorsPosition === 'bottom'
      ? this._renderIndicators(false)
      : nothing;

    // On-item indicators
    const onItemIndicators = this.showIndicators && this.showIndicatorsOnItem
      ? this._renderIndicators(true)
      : nothing;

    return html`
      <div class="uwc-gal__item-section">
        ${topIndicators}

        <div
          class=${classMap({
            'uwc-gal__item-wrapper': true,
            'uwc-gal__item-wrapper--has-hover-nav': hoverNav,
          })}
          tabindex="0"
          role="group"
          aria-roledescription="gallery"
          aria-label="Image gallery"
          @keydown=${this._onKeyDown}
          @pointerdown=${this._onPointerDown}
          @pointermove=${this._onPointerMove}
          @pointerup=${this._onPointerUp}
          @pointercancel=${this._onPointerCancel}
        >
          <!-- Prev nav area -->
          ${showNav ? html`
            <div class="uwc-gal__nav-area uwc-gal__nav-area--prev">
              ${this._renderNavBtn('prev')}
            </div>
          ` : nothing}

          <!-- Active item -->
          <div
            class=${classMap({ 'uwc-gal__item': true, 'is-animating': this._isAnimating })}
            role="img"
            aria-label=${this._liveLabel || `Image ${this._activeIndex + 1} of ${this.items.length}`}
          >
            ${activeItem != null
              ? (this.itemTemplate
                  ? this.itemTemplate(activeItem, this._activeIndex)
                  : this._defaultItemTpl(activeItem))
              : nothing}

            <!-- Caption overlay -->
            ${this.showCaption && activeItem != null ? html`
              <div class="uwc-gal__caption">
                ${this.captionTemplate
                  ? this.captionTemplate(activeItem, this._activeIndex)
                  : this._defaultCaptionTpl(activeItem)}
              </div>
            ` : nothing}

            <!-- On-item indicators -->
            ${onItemIndicators}
          </div>

          <!-- Next nav area -->
          ${showNav ? html`
            <div class="uwc-gal__nav-area uwc-gal__nav-area--next">
              ${this._renderNavBtn('next')}
            </div>
          ` : nothing}

          <!-- Fullscreen open button (only when not already in fullscreen) -->
          ${this.fullscreenEnabled && !inFullscreen ? html`
            <div class="uwc-gal__fullscreen-btn">
              <uwc-button
                icon="arrows-fullscreen"
                icon-only
                size="small"
                variant="secondary"
                outline
                aria-label="Open fullscreen"
                @uwc-click=${this._openFullscreen}
              ></uwc-button>
            </div>
          ` : nothing}

          <!-- Close button (only when in fullscreen) -->
          ${inFullscreen ? html`
            <div class="uwc-gal__close-btn">
              <uwc-button
                icon="x-lg"
                icon-only
                size="small"
                variant="secondary"
                outline
                aria-label="Close fullscreen"
                @uwc-click=${this._closeFullscreen}
              ></uwc-button>
            </div>
          ` : nothing}
        </div>

        ${bottomIndicators}
      </div>
    `;
  }

  private _renderContent(inFullscreen = false): TemplateResult {
    const pos = this.thumbnailsPosition;
    const isVert = pos === 'left' || pos === 'right';

    const contentClasses = {
      'uwc-gal__content': true,
      'uwc-gal__content--left':  pos === 'left',
      'uwc-gal__content--right': pos === 'right',
    };

    const thumbStrip = this.showThumbnails ? this._renderThumbnailStrip() : nothing;
    const itemSection = this._renderItemSection(inFullscreen);

    if (pos === 'top') {
      return html`
        <div class=${classMap(contentClasses)}>
          ${thumbStrip}
          ${itemSection}
        </div>
      `;
    }

    if (pos === 'left') {
      return html`
        <div class=${classMap(contentClasses)}>
          ${thumbStrip}
          ${itemSection}
        </div>
      `;
    }

    if (pos === 'right') {
      return html`
        <div class=${classMap(contentClasses)}>
          ${itemSection}
          ${thumbStrip}
        </div>
      `;
    }

    // Default: bottom
    return html`
      <div class=${classMap(contentClasses)}>
        ${itemSection}
        ${thumbStrip}
      </div>
    `;
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  override render() {
    return html`
      <!-- Live region for screen readers -->
      <div
        aria-live="polite"
        aria-atomic="true"
        style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap"
      >${this._liveLabel}</div>

      <slot name="header"></slot>

      <div class="uwc-gal">
        ${this._renderContent()}
      </div>

      <slot name="footer"></slot>

      ${this._isFullscreen ? html`
        <dialog
          class="uwc-gal__dialog"
          @close=${this._closeFullscreen}
          @keydown=${this._onKeyDown}
        >
          <div class="uwc-gal">
            ${this._renderContent(true)}
          </div>
        </dialog>
      ` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-galleria': UwcGalleria; }
}
